import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {Observable} from 'rxjs/Observable';
import {MatDialog, MatSnackBar} from '@angular/material';
import {ErrorResponse} from '../../../data/model/errorResponse';

import {fuseAnimations} from '../../../core/animations';
import {FuseConfigService} from '../../../core/services/config.service';
import {AuthDataManager} from '../../../data/auth.datamanager';
import {AuthResponse} from '../../../data/model/authResponse';
import {UsersDataManager} from '../../../data/users.datamanager';
import {FuseMainComponent} from '../../main.component';

import {ResetPasswordDialogComponent} from './reset/reset-password.dialog.component';

@Component({
  selector: 'fuse-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: fuseAnimations
})
export class FuseLoginComponent implements OnInit {
  loginForm: FormGroup;
  errorMessage: String;
  response: AuthResponse = new AuthResponse;
  loginFormErrors: any;
  requiresTwoFactor = false;
  submitting = false;

  constructor(
      private usersDataManager: UsersDataManager,
      private fuseConfig: FuseConfigService, private formBuilder: FormBuilder,
      private authDataManager: AuthDataManager, private router: Router,
      private mainComponent: FuseMainComponent, private dialog: MatDialog, private snackBar: MatSnackBar) {
    if (this.authDataManager.isLoggedIn()) {
      this.router.navigate([this.authDataManager.requiresTwoFactorSetup() ? '/two-factor' : '/users']);
    }
    this.fuseConfig.setSettings(
        {layout: {navigation: 'none', toolbar: 'none', footer: 'none'}});

    this.loginFormErrors = {email: {}, password: {}, twoFactorCode: {}};
  }

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required]],
      password: ['', Validators.required],
      twoFactorCode: ['']
    });

    this.loginForm.valueChanges.subscribe(() => {
      this.onLoginFormValuesChanged();
    });
  }

  onSubmit() {
    if (this.loginForm.invalid || this.submitting) {
      this.markVisibleControlsAsTouched();
      return;
    }

    if (this.requiresTwoFactor && !this.loginForm.get('twoFactorCode').value) {
      this.loginForm.get('twoFactorCode').markAsTouched();
      return;
    }

    this.submitting = true;
    this.errorMessage = '';
    const email = this.loginForm.get('email').value;
    const password = this.loginForm.get('password').value;
    const twoFactorCode = this.loginForm.get('twoFactorCode').value;

    this.authDataManager.login(email, password, this.requiresTwoFactor ? twoFactorCode : undefined)
        .subscribe(
            (response) => {
              this.submitting = false;
              this.authDataManager.setLogin(response);
              this.requiresTwoFactor = false;
              this.loginForm.get('twoFactorCode').setValue('');
              this.loginForm.get('twoFactorCode').clearValidators();
              this.loginForm.get('twoFactorCode').updateValueAndValidity();

              if (response.requires_2fa_setup) {
                this.router.navigate(['/two-factor']);
                this.mainComponent.ngOnInit();
                return;
              }

              this.completeLogin(response.token);
            },
            (err) => {
              this.submitting = false;
              if (err.requires_2fa) {
                this.requiresTwoFactor = true;
                this.loginForm.get('twoFactorCode').setValidators([Validators.required]);
                this.loginForm.get('twoFactorCode').updateValueAndValidity();
                this.errorMessage = 'Enter your 2FA code to continue.';
              } else if(err.errors != null){
                this.errorMessage = err.errors[0].message;
              }
              else{
                this.errorMessage = 'Unable to log in. Please try again.';
                console.log(err);
              }
            });
  }

  completeLogin(token: string) {
    this.usersDataManager.getLoggedInUserByToken(token)
        .subscribe(
            (res) => {
              if (res.role === 'superadmin' || res.role === 'admin' ||
                  res.role === 'instructor') {
                this.usersDataManager.setRole(res.role);
                this.usersDataManager.setName(res.name);
                this.usersDataManager.setOrganisation(res.organisation);
                this.router.navigate(['/users']);
                this.mainComponent.ngOnInit();
              } else {
                this.authDataManager.clearSession();
                this.errorMessage =
                    'You need admin privilages to login.';
              }
            },
            (err) => {
              this.authDataManager.clearSession();
              if (err && err.errors && err.errors.length > 0) {
                this.errorMessage = err.errors[0].message;
              } else if (err && (err.status === 401 || err.status === 403)) {
                this.errorMessage = 'Unauthorized.';
              } else {
                this.errorMessage = 'Unable to complete login.';
              }
              console.log(err);
            });
  }

  onLoginFormValuesChanged() {
    for (const field in this.loginFormErrors) {
      if (!this.loginFormErrors.hasOwnProperty(field)) {
        continue;
      }

      // Clear previous errors
      this.loginFormErrors[field] = {};

      // Get the control
      const control = this.loginForm.get(field);

      if (control && control.dirty && !control.valid) {
        this.loginFormErrors[field] = control.errors;
      }
    }
  }

  markVisibleControlsAsTouched() {
    if (this.requiresTwoFactor) {
      this.loginForm.get('twoFactorCode').markAsTouched();
      return;
    }

    this.loginForm.get('email').markAsTouched();
    this.loginForm.get('password').markAsTouched();
  }

    resetPasswordButtonClick() {
        const dialogRef = this.dialog.open(ResetPasswordDialogComponent, {
            height: '325px',
            width: '400px',
            data: {}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== null && result !== undefined) {
                if (result.email) {
                    this.usersDataManager
                        .resetPassword(result.email)
                        .subscribe((response) => {
                            this.showMessage('Done! New password has been sent to you.');
                            this.ngOnInit();
                        }, (err) => {
                            this.showError(err.json());
                        });
                }
            }
        });
    }

    showMessage(message: string) {
        this.snackBar.open(message, null, {duration: 5000});
    }

    showError(message: ErrorResponse) {
        if (message.errors !== null && message.errors !== undefined && message.errors.length > 0) {
              this.snackBar.open(message.errors[0].message, null, {duration: 10000});
        } else {
            this.snackBar.open('Unknown error occured', null, {duration: 10000});
        }
    }
}



// WEBPACK FOOTER //
// /tmp/build_f0fbff5b/src/app/main/content/login/login.component.ts